/**
 * EngagementRosterPanel — maps the engine's accountable C-suite roles to real
 * executive names and sets an optional client logo for co-branded deliverables.
 * Owners then read "Jane Doe · Chief Data Officer" across every export.
 * Locked once the report is finalised (re-finalise to change).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Users, ChevronDown, ChevronUp, Save, Lock, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function EngagementRosterPanel({ runId, run, onChange }) {
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [logoUrl, setLogoUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const locked = !!run?.finalised;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/assessments/roles");
      setRoles(data.roles || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setAssignments(run?.role_assignments || {});
    setLogoUrl(run?.client_logo_url || "");
  }, [run?.role_assignments, run?.client_logo_url]);

  const setRole = (role, name) => setAssignments((a) => ({ ...a, [role]: name }));

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/assessments/runs/${runId}/logo-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.client_logo_url || "");
      toast.success("Logo uploaded");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(assignments).filter(([, v]) => v && v.trim()));
      await api.put(`/admin/assessments/runs/${runId}/role-assignments`, { assignments: clean });
      await api.post(`/admin/assessments/runs/${runId}/logo`, { client_logo_url: logoUrl.trim() || null });
      toast.success("Roster & logo saved — applied to every deliverable");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save roster");
    } finally { setBusy(false); }
  };

  const fieldCls = "w-full border border-zinc-300 px-2.5 py-1.5 text-[13px] disabled:bg-zinc-100";
  const filled = Object.values(assignments).filter((v) => v && v.trim()).length;

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="engagement-roster-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="roster-toggle">
        <span className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
          <Users size={14}/> Engagement roster {filled > 0 && <span className="text-[#7737F6] normal-case tracking-normal">· {filled} named</span>}
        </span>
        {open ? <ChevronUp size={16} className="text-zinc-400"/> : <ChevronDown size={16} className="text-zinc-400"/>}
      </button>

      {open && (
        <div className="mt-4 space-y-3" data-testid="roster-form">
          {locked && (
            <div className="flex items-center gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="roster-locked">
              <Lock size={12}/> Report is finalised — roster &amp; logo are locked. Re-finalise to change.
            </div>
          )}
          <p className="text-[11px] text-zinc-500 leading-relaxed">Map an accountable role to a real name. Owners render as “Name · Role” across the board pack, executive pack and board paper.</p>

          {roles.map((role) => (
            <div key={role}>
              <label className="text-[11px] font-medium text-zinc-600">{role}</label>
              <input
                value={assignments[role] || ""}
                onChange={(e) => setRole(role, e.target.value)}
                disabled={locked}
                placeholder="Leave blank to keep the role only"
                className={fieldCls}
                data-testid={`roster-input-${role.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
              />
            </div>
          ))}

          <div className="border-t border-zinc-100 pt-3">
            <label className="text-[11px] font-medium text-zinc-600 flex items-center gap-1"><ImageIcon size={12}/> Client logo (co-branding)</label>
            <div className="flex items-center gap-2 mt-1">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" onChange={onPickLogo} disabled={locked || uploading} className="hidden" data-testid="roster-logo-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={locked || uploading} className="ek-btn ek-btn-outline text-[12px] disabled:opacity-50" data-testid="roster-logo-upload">
                <Upload size={12}/> {uploading ? "Uploading…" : "Upload logo"}
              </button>
              <span className="text-[10.5px] text-zinc-400">PNG/SVG, max 5 MB</span>
            </div>
            <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} disabled={locked} placeholder="…or paste an image URL" className={`${fieldCls} mt-2`} data-testid="roster-logo-url" />
            {logoUrl?.trim() && (
              <div className="mt-2 bg-[#0B0B0C] p-2 inline-block" data-testid="roster-logo-preview">
                <img src={logoUrl} alt="Client logo preview" style={{ maxHeight: 36, maxWidth: 160 }} />
              </div>
            )}
          </div>

          <button type="button" onClick={save} disabled={busy || locked} className="ek-btn ek-btn-primary w-full text-[13px] disabled:opacity-50" data-testid="roster-save">
            <Save size={13}/> {busy ? "Saving…" : "Save roster & logo"}
          </button>
        </div>
      )}
    </div>
  );
}
